/**
 * Transcript parsing utilities — extract structured line data from raw
 * deposition/hearing transcript text.
 *
 * Extracted to shared/ so both TranscriptView (rendering) and MetadataPalette
 * (speaker-spotlight matchItems) can use the same parsing logic.
 */

export interface TranscriptLine {
    lineNum: number | null;
    content: string;
    speaker: string | null;
    /** Original speaker identifier before formatting (e.g. "[j__john_g_roberts_jr]") */
    speakerRaw?: string;
    speakerRest: string;
    isEmpty: boolean;
    isPageBreak: boolean;
    /** Char offset of this line in the original document body */
    bodyCharOffset: number;
}

// Strip leading line numbers: "  14   Q.  How are you?" → lineNum=14
const LINE_NUM_RE = /^\s*(\d{1,3})\s{1,6}/;

// Speaker patterns: "Q." "A." "Q " "A " "MR. SMITH:" "THE WITNESS:" "BY MR. JONES:" etc.
// Period/colon is optional — some transcript services (e.g. Planet Depos) omit it.
const SPEAKER_RE = /^(Q|A|(?:MR|MRS|MS|DR|JUDGE|THE WITNESS|THE COURT|THE VIDEOGRAPHER|BY MR|BY MS|BY MRS|BY DR)\.?\s*[A-Z]*[A-Za-z'-]*)\s*[.:]?\s+/i;

// Bracket-speaker format: "[j__john_g_roberts_jr]: text" (oral arguments, hearings)
const BRACKET_SPEAKER_RE = /^\[([^\]]+)\]:\s*/;

/** Convert a bracket speaker ID to a readable name.
 *  "j__john_g_roberts_jr" → "Justice John G. Roberts Jr."
 *  "toby_crouse" → "Toby Crouse"
 */
function formatBracketSpeaker(id: string): string {
    // "j__" prefix = Justice
    const isJustice = id.startsWith('j__');
    const raw = isJustice ? id.slice(3) : id;
    const name = raw
        .split('_')
        .filter(Boolean)
        .map(part => {
            // Common suffixes — preserve casing
            if (/^(jr|sr|ii|iii|iv)$/i.test(part)) return part.charAt(0).toUpperCase() + part.slice(1) + '.';
            // Single letter = initial
            if (part.length === 1) return part.toUpperCase() + '.';
            return part.charAt(0).toUpperCase() + part.slice(1);
        })
        .join(' ');
    return isJustice ? `Justice ${name}` : name;
}

/** Detect if text uses bracket-speaker format by checking first non-header lines. */
function isBracketFormat(lines: string[]): boolean {
    let bracketLines = 0;
    let checked = 0;
    for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('=') || /^(Case ID|Title|Topic|Utterances):/i.test(trimmed)) continue;
        if (BRACKET_SPEAKER_RE.test(trimmed)) bracketLines++;
        checked++;
        if (checked >= 10) break;
    }
    return bracketLines >= 2;
}

export function isLikelyTranscript(text: string): boolean {
    const lines = text.split('\n').slice(0, 200);

    // Check bracket-speaker format first (oral arguments, hearings)
    if (isBracketFormat(lines)) return true;

    // Standard deposition format: numbered lines + Q/A/MR. speakers
    let speakerLines = 0;
    let numberedLines = 0;
    for (const line of lines) {
        if (LINE_NUM_RE.test(line)) numberedLines++;
        const stripped = line.replace(LINE_NUM_RE, '').trimStart();
        if (SPEAKER_RE.test(stripped)) speakerLines++;
    }
    return speakerLines >= 3 && numberedLines > lines.length * 0.3;
}

export function parseTranscriptLines(text: string): TranscriptLine[] {
    const rawLines = text.split('\n');

    // ── Bracket-speaker format (oral arguments, hearings) ───────────────
    // "[speaker_id]: text" — each paragraph belongs to one speaker.
    // Continuation lines (no bracket prefix) are merged into the same speaker block.
    if (isBracketFormat(rawLines)) {
        return parseBracketTranscript(rawLines);
    }

    // ── Detect gutter runs ──────────────────────────────────────────────
    // PDF text extraction often pulls the line-number column as separate
    // lines: "1\n2\n…\n25\n" before each page's content. We detect runs
    // of ≥3 consecutive ascending integers starting from 1, then merge
    // each gutter number back onto the corresponding content line that
    // follows the run (+ an optional standalone page number after the run).

    interface GutterRun { start: number; length: number; }
    const gutterRuns: GutterRun[] = [];
    const isGutter: boolean[] = new Array(rawLines.length).fill(false);
    let runStart = -1;
    let lastNum = -1;

    for (let i = 0; i <= rawLines.length; i++) {
        const trimmed = i < rawLines.length ? rawLines[i].trim() : '';
        const isNum = /^\d{1,3}$/.test(trimmed);
        const num = isNum ? parseInt(trimmed, 10) : -1;

        if (isNum && (runStart === -1 ? num === 1 : num === lastNum + 1)) {
            if (runStart === -1) runStart = i;
            lastNum = num;
        } else {
            if (runStart !== -1 && (i - runStart) >= 3) {
                const runLen = i - runStart;
                gutterRuns.push({start: runStart, length: runLen});
                for (let j = runStart; j < i; j++) isGutter[j] = true;
            }
            // Allow a new run to start at 1
            runStart = (isNum && num === 1) ? i : -1;
            lastNum = (isNum && num === 1) ? 1 : -1;
        }
    }

    // Build a map: for each content-line index after a gutter run, what
    // line number should it get?  The page number line right after the
    // gutter run is also marked as a page break.
    const gutterLineNum = new Map<number, number>();  // rawIndex → lineNum
    const isPageNum = new Set<number>();               // rawIndex of page-number lines

    for (const run of gutterRuns) {
        const afterRun = run.start + run.length;
        // The line right after the gutter run is usually a standalone page number
        let contentStart = afterRun;
        if (contentStart < rawLines.length) {
            const t = rawLines[contentStart].trim();
            if (/^\d{1,4}$/.test(t)) {
                isPageNum.add(contentStart);
                contentStart++;
            }
        }
        // Pair gutter numbers with content lines
        for (let g = 0; g < run.length; g++) {
            const contentIdx = contentStart + g;
            if (contentIdx >= rawLines.length) break;
            const gutterVal = parseInt(rawLines[run.start + g].trim(), 10);
            gutterLineNum.set(contentIdx, gutterVal);
        }
    }

    // ── Parse lines ─────────────────────────────────────────────────────
    // Track cumulative char offset in the original body text so each line
    // can be mapped to a page via page_body_offsets.
    const result: TranscriptLine[] = [];
    let bodyOffset = 0;
    for (let i = 0; i < rawLines.length; i++) {
        const lineLen = rawLines[i].length + (i < rawLines.length - 1 ? 1 : 0); // +1 for \n except last
        if (isGutter[i]) {
            bodyOffset += lineLen;
            continue;
        }

        const rawLine = rawLines[i];
        const trimmed = rawLine.trim();
        const currentOffset = bodyOffset;
        bodyOffset += lineLen;

        // Page number line (standalone number right after gutter run)
        if (isPageNum.has(i)) {
            result.push({
                lineNum: null, content: trimmed, speaker: null,
                speakerRest: '', isEmpty: false, isPageBreak: true,
                bodyCharOffset: currentOffset,
            });
            continue;
        }

        // Check for inline line number (e.g. "  14   Q. How are you?")
        const numMatch = rawLine.match(LINE_NUM_RE);
        // Prefer merged gutter number, fall back to inline
        const lineNum = gutterLineNum.get(i) ?? (numMatch ? parseInt(numMatch[1], 10) : null);
        const withoutNum = numMatch ? rawLine.slice(numMatch[0].length) : rawLine;

        // Trim before speaker match — PDF extraction can leave leading
        // whitespace on content lines (especially in gutter-format transcripts
        // where line numbers are separate from content).
        const trimmedWithoutNum = withoutNum.trimStart();
        const speakerMatch = trimmedWithoutNum.match(SPEAKER_RE);
        const speaker = speakerMatch ? speakerMatch[1].trim() : null;
        const speakerRest = speakerMatch ? trimmedWithoutNum.slice(speakerMatch[0].length) : '';
        const content = withoutNum.trim();

        // Standalone number not caught above — treat as page break
        const isPageBreak = /^\d+$/.test(trimmed) && !numMatch && !gutterLineNum.has(i);

        result.push({lineNum, content, speaker, speakerRest, isEmpty: !trimmed, isPageBreak, bodyCharOffset: currentOffset});
    }
    return result;
}

// ── Bracket-speaker parser ──────────────────────────────────────────────
// Handles "[speaker_id]: text" format. Each raw line becomes one TranscriptLine.
// Lines starting with [speaker]: get the speaker extracted; continuation lines
// (text that wraps under the same speaker) have speaker=null.

function parseBracketTranscript(rawLines: string[]): TranscriptLine[] {
    const result: TranscriptLine[] = [];
    let bodyOffset = 0;
    let lineCounter = 1;

    for (let i = 0; i < rawLines.length; i++) {
        const rawLine = rawLines[i];
        const lineLen = rawLine.length + (i < rawLines.length - 1 ? 1 : 0);
        const trimmed = rawLine.trim();
        const currentOffset = bodyOffset;
        bodyOffset += lineLen;

        // Skip header lines (metadata block before the transcript)
        if (/^(Case ID|Title|Topic|Utterances):/i.test(trimmed) || /^=+$/.test(trimmed)) {
            result.push({
                lineNum: null, content: trimmed, speaker: null,
                speakerRest: '', isEmpty: false, isPageBreak: false,
                bodyCharOffset: currentOffset,
            });
            continue;
        }

        if (!trimmed) {
            result.push({
                lineNum: null, content: '', speaker: null,
                speakerRest: '', isEmpty: true, isPageBreak: false,
                bodyCharOffset: currentOffset,
            });
            continue;
        }

        const bracketMatch = trimmed.match(BRACKET_SPEAKER_RE);
        if (bracketMatch) {
            const speaker = formatBracketSpeaker(bracketMatch[1]);
            const rest = trimmed.slice(bracketMatch[0].length);
            result.push({
                lineNum: lineCounter++,
                content: trimmed,
                speaker,
                speakerRaw: `[${bracketMatch[1]}]`,
                speakerRest: rest,
                isEmpty: false,
                isPageBreak: false,
                bodyCharOffset: currentOffset,
            });
        } else {
            // Continuation line under the same speaker
            result.push({
                lineNum: lineCounter++,
                content: trimmed,
                speaker: null,
                speakerRest: '',
                isEmpty: false,
                isPageBreak: false,
                bodyCharOffset: currentOffset,
            });
        }
    }

    return result;
}
